/**
 * Sprint 97 — DuitNow bulk-payment CSV exporter.
 *
 * The dashboard `/superadmin/payouts` page bundles a batch of pending
 * payouts into a single CSV that the platform owner uploads to their
 * bank's bulk-transfer portal (Maybank2u, CIMB BizChannel, etc).
 *
 * Different MY banks accept slightly different column orders, but a
 * common workable shape is:
 *
 *   No, Recipient Name, ID Type, ID, Amount, Reference, Payment Description
 *
 * - **ID Type** is `DUITNOW_ID` for proxy IDs (NRIC / mobile / SSM) or
 *   `ACCOUNT` for direct bank-account transfers.
 * - **ID** is the proxy value or the account number.
 * - **Amount** is in RM with 2 decimals (banks reject sen-as-int).
 * - **Reference** is the payout doc id (so reconciliation can map a
 *   bank line back to a Firestore payout).
 *
 * The bank portal CSV format is documented in
 * docs/runbooks/agent-payout-day.md — keep both in sync if either
 * changes.
 *
 * Pure: no I/O, no Date.now() — caller passes generatedAtMs so a
 * test pin works deterministically.
 */

import { csvCell } from '../utils/csv.js';

export interface DuitNowCsvRow {
  /** Display name for the recipient — appears on the recipient's bank statement. */
  readonly recipientName: string;
  /**
   * DuitNow proxy ID (NRIC / passport / mobile / SSM) OR bank account
   * number. `idType` distinguishes which.
   */
  readonly idType: 'DUITNOW_ID' | 'ACCOUNT';
  readonly id: string;
  /**
   * Bank name — only used when `idType === 'ACCOUNT'`. Many MY banks
   * accept a free-text bank name; some require a bank code (deferred:
   * if we hit that, add a `bankCode?: string` field and map per-bank).
   */
  readonly bankName?: string;
  /** Amount in sen. Converted to RM with 2 decimals on render. */
  readonly amountSen: number;
  /** Reference field — the payout doc id keeps reconciliation simple. */
  readonly reference: string;
  /** Optional descriptor visible on the recipient's bank statement. */
  readonly description?: string;
}

export interface DuitNowCsvInput {
  readonly rows: readonly DuitNowCsvRow[];
  /** Now in ms-since-epoch — used in the filename suggestion only. */
  readonly generatedAtMs: number;
  /** Optional brand name to make the filename owner-distinguishable. */
  readonly brandName?: string;
}

export interface DuitNowCsvOutput {
  readonly csv: string;
  /** Suggested filename (no path) — caller hooks into a download link. */
  readonly filename: string;
  /** Total RM of every row, for sanity-checking before upload. */
  readonly totalRm: number;
}

/**
 * Render the CSV. Header row always emitted (banks won't autodetect
 * column order from raw data). Empty `rows` returns just the header
 * plus a single sentinel comment row so a curious operator gets a
 * meaningful file instead of an empty one.
 */
export function renderDuitNowCsv(input: DuitNowCsvInput): DuitNowCsvOutput {
  const header = [
    'No',
    'Recipient Name',
    'ID Type',
    'ID',
    'Bank',
    'Amount (RM)',
    'Reference',
    'Description',
  ];
  const lines: string[] = [header.map(csvCell).join(',')];

  if (input.rows.length === 0) {
    lines.push('# no payouts in this batch — nothing to upload');
  } else {
    input.rows.forEach((row, i) => {
      const rm = (row.amountSen / 100).toFixed(2);
      lines.push(
        [
          String(i + 1),
          csvCell(row.recipientName),
          row.idType,
          csvCell(row.id),
          csvCell(row.bankName ?? ''),
          rm,
          csvCell(row.reference),
          csvCell(row.description ?? ''),
        ].join(','),
      );
    });
  }

  const totalSen = input.rows.reduce((acc, r) => acc + r.amountSen, 0);
  const totalRm = totalSen / 100;

  const date = new Date(input.generatedAtMs);
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const brandSlug = (input.brandName ?? 'platform')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const filename = `${brandSlug}-payouts-${yyyy}${mm}${dd}.csv`;

  return {
    csv: lines.join('\r\n') + '\r\n',
    filename,
    totalRm,
  };
}
